import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, Empty } from "@/components/shared";
import { BookOpen, Plus, X, FileText, Eye } from "lucide-react";
import { toast } from "sonner";

const CATS = ["All", "Datasheet", "Guide", "Manual", "Compliance", "Reference", "Policy"];

export default function Library() {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(false);

  const load = () => api.get("/library/docs", { params: cat !== "All" ? { category: cat } : {} }).then((r) => setDocs(r.data));
  useEffect(() => { load(); }, [cat]);

  return (
    <div>
      <PageHeader title="Technical Library" subtitle="Datasheets, installation guides and compliance docs (Priority-2, live CMS)." testid="library-page"
        actions={<button data-testid="new-doc-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> Add Document</button>} />

      <div className="flex flex-wrap gap-2 mb-5">
        {CATS.map((c) => <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${cat === c ? "text-white" : "bg-white border border-slate-200 text-slate-600"}`} style={cat === c ? { background: "var(--navy-800)" } : {}}>{c}</button>)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {docs.map((d) => (
          <div key={d.id} className="card-surface p-5 flex flex-col">
            <div className="flex items-start justify-between">
              <span className="h-10 w-10 rounded-lg grid place-items-center bg-orange-50"><FileText size={18} style={{ color: "var(--copper)" }} /></span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold uppercase">{d.category}</span>
            </div>
            <h4 className="font-semibold text-slate-800 mt-3">{d.title}</h4>
            <p className="text-xs text-slate-500 mt-1 flex-1">{d.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(d.tags || []).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">#{t}</span>)}
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-400 mt-3"><Eye size={12} /> {d.views} views</div>
          </div>
        ))}
      </div>
      {docs.length === 0 && <Empty />}

      {open && <DocDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); toast.success("Document added"); }} />}
    </div>
  );
}

function DocDialog({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", category: "Datasheet", description: "", tags: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try { await api.post("/library/docs", { ...f, tags: f.tags.split(",").map((x) => x.trim()).filter(Boolean) }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="doc-dialog">
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900">Add Document</h3><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <div className="space-y-3">
          <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="doc-title" />
          <select value={f.category} onChange={(e) => set("category", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">{CATS.slice(1).map((c) => <option key={c}>{c}</option>)}</select>
          <textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Tags (comma separated)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <button onClick={submit} data-testid="doc-submit" className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Add Document</button>
        </div>
      </div>
    </div>
  );
}
